import { OrderCard } from 'src/components/Orders/OrderCards/orderCard';
import { Order } from 'src/utils/interfaces/interfaces';
import { useNavigate } from 'react-router-dom';
import {
  useGetOrdersListByCompanyMutation,
  useSearchOrdersMutation,
} from 'src/services/orders/orders';
import { Input } from 'src/components/UI/Forms/Inputs';
import { useState, useEffect, useRef } from 'react';
import {
  APPROVED,
  COMPLETED,
  IN_TRANSIT,
  READY,
  DRAFT,
} from 'src/constants/statusConstants';
import { useTranslation } from 'react-i18next';
import {
  Pagination,
  Tabs,
  Tab,
  Chip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  Button,
} from '@heroui/react';
import i18n from 'src/i18n';
import OrdersTable from '../ordersListView';
import CreateOrderForm from '../createOrderForm';
import Reports from 'src/pages/reports/reports';
import { TbReportSearch } from 'react-icons/tb';
import OrdersAccordionView from '../orderAccordianView';
import { getParams, getStatusesForOrderType } from 'src/utils/orders';
import { ShipperOrderDetailsSidebar } from './orderDetailsSidebar';
import { FaPlus } from 'src/icons';

export interface IOrdersProps { }

type OrderType = 'completed' | 'ongoing' | 'other' | 'draft';
interface Meta {
  count: number;
  limit: number;
  page: number;
}

interface OrdersState {
  completed: Order[];
  ongoing: Order[];
  draft: Order[];
  other: Order[];
}

interface MetaState {
  completed: Meta;
  ongoing: Meta;
  other: Meta;
  draft: Meta;
}

export function ShipperOrders() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [orders, setOrders] = useState<OrdersState>({
    completed: [],
    ongoing: [],
    draft: [],
    other: [],
  });

  const [meta, setMeta] = useState<MetaState>({
    completed: { count: 0, limit: 20, page: 1 },
    ongoing: { count: 0, limit: 20, page: 1 },
    other: { count: 0, limit: 20, page: 1 },
    draft: { count: 0, limit: 20, page: 1 },
  });

  const [isListView, setIsListView] = useState(() => {
    const savedIsListView = window.localStorage.getItem('isListView');
    return savedIsListView !== null ? JSON.parse(savedIsListView) : true;
  });

  const [type, setTypeView] = useState<OrderType>(() => {
    const savedType = window.localStorage.getItem('activeTab') as OrderType;
    return savedType &&
      ['completed', 'ongoing', 'other', 'draft'].includes(savedType)
      ? savedType
      : 'draft';
  });

  const [activeOrder, setActiveOrder] = useState<string | null>(() => {
    return window.localStorage.getItem('activeOrder');
  });

  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    return window.localStorage.getItem('searchTerm') || '';
  });

  const [getOrdersList] = useGetOrdersListByCompanyMutation({});
  const [searchOrders] = useSearchOrdersMutation();

  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);

  // Sidebar State
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const clearActiveOrderSelection = () => {
    window.localStorage.removeItem('activeOrder');
    setActiveOrder(null);
    setSelectedOrderId(null);
  };

  // -----------------------
  // fetchOrders function
  // -----------------------
  const fetchOrders = async (
    orderType: OrderType,
    page: number,
    search: string = '',
    entity?: string
  ) => {
    setIsOrderLoading(true);

    try {
      if (orderType === 'draft') {
        const params = getParams({ orderType, page, meta, entity });

        const draftRes = await getOrdersList({ status: DRAFT, ...params }).unwrap();

        const totalCount = draftRes?.meta?.count || 0;

        setOrders((prev) => ({
          ...prev,
          draft: draftRes?.response ?? [],
        }));

        setMeta((prev) => ({
          ...prev,
          draft: {
            count: totalCount,
            limit: meta.draft.limit,
            page,
          },
        }));

        return;
      }

      const statuses = getStatusesForOrderType(orderType);
      const params = getParams({ orderType, page, meta, entity });

      const allResponses = await Promise.all(
        statuses.map((status) => getOrdersList({ status, ...params }).unwrap())
      );

      const allOrders = allResponses.flatMap((res) => res.response || []);
      const totalCount = allResponses.reduce(
        (sum, res) => sum + (res.meta?.count || 0),
        0
      );

      setOrders((prev) => ({
        ...prev,
        [orderType]: allOrders,
      }));

      setMeta((prev) => ({
        ...prev,
        [orderType]: {
          ...meta[orderType],
          count: totalCount,
          limit: meta[orderType].limit,
          page,
        },
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsOrderLoading(false);
    }
  };

  // -----------------------
  // fetchSearchOrders function
  // -----------------------
  const fetchSearchOrders = async (
    orderType: OrderType,
    search: string,
    entity?: string
  ) => {
    setIsOrderLoading(true);

    try {
      const commonParams = {
        word: search,
        ...(entity ? { entity } : {}),
      };

      if (orderType === 'other') {
        // Perform parallel search for APPROVED and READY
        const [approvedSearchRes, readySearchRes] = await Promise.all([
          searchOrders({ status: APPROVED, ...commonParams }).unwrap(),
          searchOrders({ status: READY, ...commonParams }).unwrap(),
        ]);

        const combinedResults = [
          ...(approvedSearchRes ?? []),
          ...(readySearchRes ?? []),
        ];

        setOrders((prev) => ({
          ...prev,
          other: combinedResults,
        }));
      } else {
        // Determine status based on order type
        const statusMap: Record<OrderType, string> = {
          draft: DRAFT,
          ongoing: IN_TRANSIT,
          completed: COMPLETED,
          other: APPROVED, // never hit due to above condition, but required by type
        };

        const response = await searchOrders({
          status: statusMap[orderType],
          ...commonParams,
        }).unwrap();

        setOrders((prev) => ({
          ...prev,
          [orderType]: response ?? [],
        }));
      }
    } catch (error) {
      console.error('Search Orders Failed:', error);
    } finally {
      setIsOrderLoading(false);
    }
  };

  const resetPreservedData = () => {
    window.localStorage.removeItem('activeTab');
    window.localStorage.removeItem('activeOrder');
    window.localStorage.removeItem('activePage');
    window.localStorage.removeItem('scrollPosition');
  };

  useEffect(() => {
  }, [orders]);

  useEffect(() => {
    if (type === 'draft' && !searchTerm) {
      const intervalId = setInterval(async () => {
        setIsAutoRefreshing(true);
        await fetchOrders('draft', meta.draft.page);
        setIsAutoRefreshing(false);
      }, 30000);

      return () => clearInterval(intervalId);
    }
  }, [type, meta.draft.page, searchTerm, selectedEntity]);

  useEffect(() => {
    if (containerRef.current) {
      const savedScrollPosition = window.localStorage.getItem('scrollPosition');
      if (savedScrollPosition) {
        containerRef.current.scrollTo(0, parseInt(savedScrollPosition, 10));
      }
    }
  }, [type, meta]);

  // Handle restoring page from sessionStorage on mount
  useEffect(() => {
    const fromOrdersPage = sessionStorage.getItem('fromOrdersPage');
    if (fromOrdersPage) {
      const savedPage = window.localStorage.getItem('activePage');
      if (savedPage) {
        setMeta((prev) => ({
          ...prev,
          [type]: { ...prev[type], page: Number(savedPage) },
        }));
      }
      sessionStorage.removeItem('fromOrdersPage');
    } else {
      resetPreservedData();
    }
    // The reactive useEffect below will handle the actual fetch
  }, []);

  const onChangeSearch = (searchString: string) => {
    setSearchTerm(searchString);
    if (searchString) {
      window.localStorage.setItem('searchTerm', searchString);
      fetchSearchOrders(
        type,
        searchString,
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    } else {
      window.localStorage.removeItem('searchTerm');
      fetchOrders(
        type,
        meta[type].page,
        '',
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    }
  };

  const ListView = () => {
    window.localStorage.removeItem('activeOrder');
    setActiveOrder(null);
    setIsListView((prevIsListView: boolean) => {
      const newIsListView = !prevIsListView;
      window.localStorage.setItem('isListView', JSON.stringify(newIsListView));
      return newIsListView;
    });
  };

  const onTabChange = (newType: string | number) => {
    setTypeView(newType as OrderType);
    setMeta((prev) => ({
      ...prev,
      [newType as OrderType]: {
        ...prev[newType as OrderType],
        page: 1,
      },
    }));
    resetPreservedData();
    // The reactive useEffect will handle fetching when type/meta changes
  };

  useEffect(() => {
    if (searchTerm) {
      fetchSearchOrders(
        type,
        searchTerm,
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    } else {
      fetchOrders(
        type,
        meta[type].page,
        '',
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    }
  }, [type, meta[type]?.page, searchTerm, selectedEntity]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [type]);

  const handlePageClick = (newPage: number) => {
    resetPreservedData();
    setMeta((prev) => ({
      ...prev,
      [type]: { ...prev[type], page: newPage },
    }));
    // The reactive useEffect will handle fetching when meta[type].page changes
  };

  const totalPageCount = Math.ceil(meta[type].count / meta[type].limit);

  const handleOrderClick = (order: Order) => {

    sessionStorage.setItem('fromOrdersPage', 'true');
    window.localStorage.setItem('activeTab', type);
    window.localStorage.setItem('activeOrder', order._id);
    window.localStorage.setItem('activePage', meta[type].page.toString());
    window.localStorage.setItem(
      'scrollPosition',
      containerRef.current?.scrollTop.toString() || '0'
    );
    setActiveOrder(order._id);

    // Open sidebar instead of navigating
    setSelectedOrderId(order._id);
    setIsSidebarOpen(true);
  };

  const handleOrderCreated = () => {
    // Re-fetch orders after successful creation
    fetchOrders(
      type,
      meta[type].page,
      searchTerm,
      selectedEntity !== 'all' ? selectedEntity : undefined
    );
  };

  return (
    <div className="flex w-full px-6 pt-4" style={{ backgroundColor: "var(--bg-base)" }}>
      <div
        className="w-full h-screen overflow-y-auto overflow-x-hidden" style={{ color: "var(--text-main)" }}
        ref={containerRef}
      >
        {/* Sticky Header and Tabs */}
        <div className="sticky top-0 z-40" style={{ backgroundColor: "var(--bg-base)" }}>
          {/* Header */}
          <div className="pt-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl">{t('orders_page.title')}</h3>
              </div>
              <div>
                <div className="flex justify-end mt-4">
                  <Input
                    type="text"
                    placeholder={t('orders_page.search_textbox')}
                    inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChangeSearch(e.target.value)
                    }
                    iconUrl="/assets/icons/search.svg"
                    defaultValue={searchTerm}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs with icons and counts */}
          <div className="flex items-center w-full flex-row border-b border-[var(--border-light)]">
            <Tabs
              aria-label="Orders Tabs"
              variant="underlined"
              selectedKey={type}
              onSelectionChange={(key) => onTabChange(key)}
              classNames={{
                tabList:
                  'gap-10 w-full relative rounded-none p-0 overflow-hidden',
                cursor: "w-full bg-[var(--color-primary)]",
                tab: 'max-w-fit px-1 h-14',
                tabContent:
                  'text-[var(--text-muted)] group-data-[selected=true]:text-[var(--text-main)] transition-all duration-300',
              }}
            >
              <Tab
                key="draft"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.draft_orders')}
                    </span>
                    {type === 'draft' &&
                      !searchTerm &&
                      meta.draft.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-sm border border-[var(--border-light)]" style={{ backgroundColor: "var(--bg-overlay)", color: "var(--text-main)" }}
                        >
                          {meta.draft.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>

              <Tab
                key="other"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.ready_orders')}
                    </span>
                    {type === 'other' &&
                      !searchTerm &&
                      meta.other.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-sm border border-[var(--border-light)]" style={{ backgroundColor: "var(--bg-overlay)", color: "var(--text-main)" }}
                        >
                          {meta.other.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>

              <Tab
                key="ongoing"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.ongoing_orders')}
                    </span>
                    {type === 'ongoing' &&
                      !searchTerm &&
                      meta.ongoing.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-sm border border-[var(--border-light)]" style={{ backgroundColor: "var(--bg-overlay)", color: "var(--text-main)" }}
                        >
                          {meta.ongoing.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>

              <Tab
                key="completed"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.completed_orders')}
                    </span>
                    {type === 'completed' &&
                      !searchTerm &&
                      meta.completed.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-sm border border-[var(--border-light)]" style={{ backgroundColor: "var(--bg-overlay)", color: "var(--text-main)" }}
                        >
                          {meta.completed.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>
            </Tabs>

            {/* Action Buttons on the far right */}
            <div className="flex gap-4 mb-4">
              <Button
                variant="solid"
                startContent={<FaPlus />}
                className="rounded-md p-2 font-semibold transition-colors duration-200" style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
                onPress={() => setIsCreateOrderOpen(true)}
              >
                {t('orders_page.create_order')}
              </Button>

              <Button
                variant="solid"
                className="rounded-md p-2 font-semibold transition-colors duration-200" style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
                onPress={() => setIsReportOpen(true)}
                startContent={<TbReportSearch className="w-5 h-5" />}
              >
                {t('orders_page.report')}
              </Button>
              {/* Toggle List/Card View */}
              {/* <Button
                variant="solid"
                isIconOnly
                onPress={ListView}
                className=" bg-bluePrimary-100/[0.9] rounded-md p-0"
              >
                {isListView ? (
                  <GridViewIcon
                    className="text-white"
                    titleAccess="Card View"
                    fontSize="small"
                  />
                ) : (
                  <ListIcon
                    className="text-white"
                    titleAccess="List View"
                    fontSize="small"
                  />
                )}
              </Button> */}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div>
          {type === 'draft' && (
            <div className="transition-all duration-300">
              {orders.draft && orders.draft.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersAccordionView
                      type="draft"
                      orders={orders.draft}
                      isLoading={!orders.draft.length && isOrderLoading}
                      onOrderClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.draft.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => handleOrderClick(order)}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* "Ready" Orders Tab (key="other") */}
          {type === 'other' && (
            <div className="transition-all duration-300">
              {orders.other && orders.other.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.other}
                      type={APPROVED}
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.other.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => {
                          // Only allow click if status is APPROVED
                          // You can customize this check to also allow READY
                          if (
                            order.status === APPROVED ||
                            order.status === READY
                          ) {
                            handleOrderClick(order);
                          }
                        }}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ongoing Orders Tab (key="ongoing") */}
          {type === 'ongoing' && (
            <div>
              {orders.ongoing && orders.ongoing.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.ongoing}
                      type="ongoing"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.ongoing.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => handleOrderClick(order)}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Completed Orders Tab (key="completed") */}
          {type === 'completed' && (
            <div>
              {orders.completed && orders.completed.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.completed}
                      type="completed"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.completed.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => handleOrderClick(order)}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6">
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center">
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {!searchTerm && orders[type].length > 0 && (
            <div
              className={`fixed bottom-0 w-full flex justify-center p-4 z-10`} style={{ backgroundColor: "var(--bg-base)" }}
            >
              <Pagination
                variant="flat"
                key={type}
                color="primary"
                size={'md'}
                showShadow={false}
                showControls
                total={totalPageCount}
                initialPage={1}
                page={meta[type].page}
                onChange={(newPage) => handlePageClick(newPage)}
                isDisabled={isOrderLoading}
                classNames={{
                  next: `bg-[var(--bg-overlay)] text-[var(--text-main)] ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'
                    }`,
                  prev: `bg-[var(--bg-overlay)] text-[var(--text-main)] ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'
                    }`,
                  item: 'bg-[var(--bg-overlay)] text-[var(--text-main)] m-1 shadow-sm',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create Order Modal */}
      <Modal
        size="5xl"
        isOpen={isCreateOrderOpen}
        onClose={() => setIsCreateOrderOpen(false)}
        className="p-0 w-[85vw] h-[85vh] max-w-none shadow-card border border-[var(--border-light)]"
        style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}
        backdrop="blur"
        classNames={{
          closeButton:
            'm-1 rounded-full bg-[var(--bg-overlay)] p-2 text-[var(--text-main)] hover:bg-[var(--bg-surface-2)]',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader
                className="font-bold text-2xl mt-1 ps-8"
                style={{ color: 'var(--text-main)' }}
              >
                {t('orders_page.create_order')}
              </ModalHeader>
              <ModalBody
                className="overflow-y-auto"
                style={{ backgroundColor: 'var(--bg-base)' }}
              >
                <CreateOrderForm
                  onClose={() => setIsCreateOrderOpen(false)}
                  onOrderCreated={handleOrderCreated}
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        size="full"
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        className="p-0 shadow-card border"
        style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-light)', color: 'var(--text-main)' }}
        backdrop="blur"
        classNames={{
          closeButton:
            'm-1 rounded-full bg-[var(--bg-overlay)] p-2 text-[var(--text-main)] hover:bg-[var(--bg-surface-2)]',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader
                className="font-bold text-2xl mt-1 ps-8"
                style={{ color: 'var(--text-main)' }}
              >
                {t('orders_page.report')}
              </ModalHeader>
              <ModalBody className="overflow-y-auto">
                <Reports />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>

      <ShipperOrderDetailsSidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          clearActiveOrderSelection();
        }}
        orderId={selectedOrderId}
      />
    </div>
  );
}

export default ShipperOrders;
